import { Component, Input } from '@angular/core';
import { statusTypes } from '@app/origami-api/constants/statuses';
import { Converter } from '@app/shared/utils/converter';
import { CollaborationReviewStatus, Field, TopicTypeId } from '@app/origami-api/constants';
import { BudgetsAndContractsModels } from '@app/ti-components/budgets-and-contracts/models/budgets-and-contracts.models';
import { CountriesModel } from '@app/pages/main/pages/startup-countries/models/countries.interfaces';
import { IrbEcModel } from '@app/pages/main/pages/startup-irb-ec/models/interfaces/irb-ec.interfaces';
import {
  SubmissionProfileSiteStatus,
  SubmissionProfileStatus,
} from '@app/origami-api/services/submission-profile.service';
import { AmendmentStatus } from '@app/pages/main/pages/startup-amendments/components/amendments-list/amendments-list.models';
import { InvestigativeSite } from '@app/origami-api/constants/investigative-site';
import { SSUDocuments } from '@app/pages/main/pages/startup-documents/models/s-s-u-documents';
import { StudyProfileStatus } from '@app/origami-api/constants/study-profile';
import { CollaborationDocumentReviewStatus } from '@app/origami-api/constants/collaboration-document-review-status';
import { AcknowledgmentDocumentStatus } from '@app/origami-api/constants/acknowledgment-status';
import {
  getColorForCountryMilestoneTemplateStatusDomain,
  getColorForCountryMilestoneTemplateStatusRoom,
  getColorForCountryWorkflowTemplateStatusDomain,
  getColorForCountryWorkflowTemplateStatusRoom,
  getColorForQuery,
  getColorForSiteVisit,
  getColorForUser,
  getGeneralStatusColor,
} from './status-helpers';
import { ActionColors, StatusColors } from '@app/shared/ui/simple-components/status/status-models';
import { WorkflowActionColor } from '@app/origami-api/constants/workflow-action-color';
import { ServerTaskQueue } from '@app/origami-api/services/server-task-queue.service';
import { DateFormatStore } from '@app/session/date-format-store';
import { TimeZoneService } from '@app/services/time-zone.service';
import { getOrganizationStatusColorFromStatusTypeId } from '@app/pages/main/pages/organizations/utils/organization-status-appearance';
import moment from 'moment';
import BudgetsAndContractsStatus = BudgetsAndContractsModels.BudgetsAndContractsStatus;
import CountryStatus = CountriesModel.CountryStatus;
import InstitutionalReviewBoardStatus = IrbEcModel.InstitutionalReviewBoardStatus;
import ContactStatus = InvestigativeSite.ContactStatus;
import SSUDocumentsStatuses = SSUDocuments.SSUDocumentsStatuses;

export enum NonTopicEntityNames {
  CollaborationDocumentReviewStatus = 'CollaborationDocumentReviewStatus',
  AcknowledgmentStatus = 'AcknowledgmentStatus',
  CollaborationReviewStatus = 'CollaborationReviewStatus',
  WorkflowStageStatusColor = 'WorkflowStageStatusColor',
  ServerTaskQueue = 'ServerTaskQueue',
}

export enum TiStatusView {
  outline = 'outline',
  filled = 'filled',
  text = 'text',
}

export interface TiStatusOptions {
  statusId: number;
  status: string;
  topicTypeId: number;
}

@Component({
  standalone: false,
  selector: 'ti-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.sass'],
})
export class StatusComponent implements TiStatusOptions {
  public statusDateTime: string;
  private readonly camelCaseRegex = /[a-z]+[A-Z][a-z]+/;
  private readonly capitalLetterRegex = /(?=[A-Z])/;
  protected _status: string; // use like title
  @Input()
  get status(): statusTypes | string {
    return this._status;
  }
  set status(status: statusTypes | string) {
    this._status = this.toSpaceSeparatedString(status);
  }
  @Input() public isDomainStatus: boolean;
  @Input() public statusId: number;
  @Input() public topicTypeId: number;
  @Input() public entityName: string;
  @Input() public forceColor: StatusColors | ActionColors;
  @Input() public icon: string;
  @Input() public statusView: TiStatusView = TiStatusView.filled;
  @Input() public fullView = false;
  @Input() public set statusDateField(field: { value: string; flags: number }) {
    if (!field?.value) {
      this.statusDateTime = null;
      return;
    }
    const applyTimeZone = Field.Is.applyTimeZone(field.flags);
    const useTimePart = Field.Is.useTimePart(field.flags);
    const serverDate = Converter.DateAndTime.parseStringToJSDate(field.value);
    const displayDate = applyTimeZone ? this.timeZoneService.convertServerDateToRoomDate(serverDate) : serverDate;
    const format = useTimePart ? DateFormatStore.dateTimeFormat : DateFormatStore.dateFormat;
    this.statusDateTime = moment(displayDate).format(format);
  }
  @Input() public blockHeight: number;
  @Input() public outerIcon = false;
  @Input() public leftIcon = true;
  @Input() public iconTooltipText: string;
  @Input() public suffix: string;

  constructor(private timeZoneService: TimeZoneService) {}

  public get hasResolvedColor(): boolean {
    if (this.forceColor) return true;
    return typeof this.statusId !== 'undefined' && this.statusId !== null && (!!this.topicTypeId || !!this.entityName);
  }

  public get chipViewClass(): string {
    return this.hasResolvedColor ? `ti-status__chip__${this.statusView}` : null;
  }

  public get colorVar(): string {
    if (!this.hasResolvedColor) return null;
    const color = this.forceColor ?? this.calculateStatusColor();
    return color ? `var(${color})` : null;
  }

  public get showIcon(): boolean {
    return !!this.icon && (this.hasResolvedColor || !!this.status);
  }

  public formatStatusToClass(status): string {
    const className = status?.toLowerCase().split(' ').join('_');
    return this.suffix ? `${className}_${this.suffix}` : className;
  }

  private toSpaceSeparatedString(targetString: string): string {
    return this.camelCaseRegex.test(targetString)
      ? targetString.split(this.capitalLetterRegex).join(' ')
      : targetString;
  }

  public calculateStatusColor(): StatusColors | ActionColors {
    if (this.topicTypeId) {
      switch (this.topicTypeId) {
        case TopicTypeId.Budget:
        case TopicTypeId.Contract: {
          return this.getColorForBudgetOrContract();
        }
        case TopicTypeId.Country:
        case TopicTypeId.CountryInDataRoom:
        case TopicTypeId.CountryStatus: {
          return this.getColorForCountry();
        }
        case TopicTypeId.InstitutionalReviewBoard:
        case TopicTypeId.InstitutionalReviewBoardInDataRoom:
        case TopicTypeId.InstitutionalReviewBoardStatus: {
          return this.getColorForInstitutionalReviewBoard();
        }
        case TopicTypeId.InvestigativeSite:
        case TopicTypeId.InvestigativeSiteInDataRoom:
        case TopicTypeId.InvestigativeSiteStatus: {
          return this.getColorForInvestigativeSite();
        }
        case TopicTypeId.CountryAmendment:
        case TopicTypeId.IRBAmendment:
        case TopicTypeId.InvestigativeSiteAmendment: {
          return this.getColorForCountryOrIrbOrSiteAmendment();
        }
        case TopicTypeId.SubmissionProfile:
        case TopicTypeId.IRBSubmissionProfile:
        case TopicTypeId.SubmissionProfileStatus: {
          return this.getColorForSubmissionProfile();
        }
        case TopicTypeId.AmendmentProfile: {
          return this.getColorForAmendmentProfile();
        }
        case TopicTypeId.Contact: {
          return this.getColorForContact();
        }
        case TopicTypeId.User: {
          return getColorForUser(this.statusId);
        }
        case TopicTypeId.MissingDocument: {
          return this.getColorForMissingDocument();
        }
        case TopicTypeId.Document:
        case TopicTypeId.DocumentStatus: {
          return this.getColorForDocument();
        }
        case TopicTypeId.SSUDocument: {
          return this.getColorForSSUDocument();
        }
        case TopicTypeId.StudyProfile:
        case TopicTypeId.StudyStatus: {
          return this.getColorForStudyProfile();
        }
        case TopicTypeId.IRBSubmissionProfileInvSite: {
          return this.getColorForIRBSubmissionProfileInvSite();
        }
        case TopicTypeId.Query:
        case TopicTypeId.RecordQuery: {
          return getColorForQuery(this.statusId);
        }

        case TopicTypeId.OrganizationStatus: {
          return this.getColorForClientOrganization();
        }

        case TopicTypeId.ActionItemStatus:
        case TopicTypeId.CapaStatus:
        case TopicTypeId.QMSAuditStatus:
        case TopicTypeId.QMSFindingStatus:
        case TopicTypeId.QMSSupplierQualificationStatus:
        case TopicTypeId.WorkflowStatus:
        case TopicTypeId.IncidentStatus:
        case TopicTypeId.Incident: {
          return getGeneralStatusColor(this.statusId);
        }
        case TopicTypeId.CountryWorkflowMilestone:
        case TopicTypeId.CountryMilestoneTemplate: {
          return this.isDomainStatus
            ? getColorForCountryMilestoneTemplateStatusDomain(this.statusId)
            : getColorForCountryMilestoneTemplateStatusRoom(this.statusId);
        }
        case TopicTypeId.CountryWorkflowTemplate: {
          return this.isDomainStatus
            ? getColorForCountryWorkflowTemplateStatusDomain(this.statusId)
            : getColorForCountryWorkflowTemplateStatusRoom(this.statusId);
        }
        case TopicTypeId.MonitoringVisitLog: {
          return getColorForSiteVisit(this.statusId);
        }
      }
    }
    switch (this.entityName) {
      case NonTopicEntityNames.CollaborationDocumentReviewStatus: {
        return this.getColorForCollaborationDocumentReviewStatus();
      }
      case NonTopicEntityNames.AcknowledgmentStatus:
        return this.getColorForDocument();
      case NonTopicEntityNames.CollaborationReviewStatus: {
        return this.getColorForCollaborationReviewStatus();
      }
      case NonTopicEntityNames.WorkflowStageStatusColor: {
        return this.getColorForWorkflowStageStatusColor();
      }
      case NonTopicEntityNames.ServerTaskQueue: {
        return this.getColorForServerTaskQueueStatus();
      }
    }
  }

  private getColorForBudgetOrContract(): StatusColors {
    switch (this.statusId) {
      case BudgetsAndContractsStatus.Draft:
        return StatusColors.StatusesGrey;
      case BudgetsAndContractsStatus.UnderReview:
        return StatusColors.StatusesPink;
      case BudgetsAndContractsStatus.Revisions:
        return StatusColors.StatusesOrange;
      case BudgetsAndContractsStatus.SentForSignature:
        return StatusColors.StatusesDandellion;
      case BudgetsAndContractsStatus.Finalized:
        return StatusColors.StatusesGreen;
    }
  }

  private getColorForCountry(): StatusColors {
    switch (this.statusId) {
      case CountryStatus.Planned:
        return StatusColors.StatusesOrange;
      case CountryStatus.Submitted:
        return StatusColors.StatusesPink;
      case CountryStatus.Active:
        return StatusColors.StatusesGreen;
      case CountryStatus.Closed:
        return StatusColors.StatusesRed;
      case CountryStatus.OnHold:
        return StatusColors.StatusesGrey;
      case CountryStatus.Canceled:
        return StatusColors.StatusesDandellion;
      case CountryStatus.Rejected:
        return StatusColors.StatusesRed;
    }
  }

  private getColorForCountryOrIrbOrSiteAmendment(): StatusColors {
    switch (this.statusId) {
      case AmendmentStatus.Pending:
        return StatusColors.StatusesOrange;
      case AmendmentStatus.Completed:
        return StatusColors.StatusesGreen;
    }
  }

  private getColorForClientOrganization(): StatusColors {
    return getOrganizationStatusColorFromStatusTypeId(this.statusId);
  }

  private getColorForInstitutionalReviewBoard(): StatusColors {
    switch (this.statusId) {
      case InstitutionalReviewBoardStatus.Pending:
        return StatusColors.StatusesOrange;
      case InstitutionalReviewBoardStatus.Submitted:
        return StatusColors.StatusesPink;
      case InstitutionalReviewBoardStatus.ConditionalApproval:
        return StatusColors.StatusesGreen;
      case InstitutionalReviewBoardStatus.Approved:
        return StatusColors.StatusesGreen;
      case InstitutionalReviewBoardStatus.Rejected:
        return StatusColors.StatusesRed;
    }
  }

  private getColorForSubmissionProfile(): StatusColors {
    switch (this.statusId) {
      case SubmissionProfileStatus.Pending:
        return StatusColors.StatusesOrange;
      case SubmissionProfileStatus.Submitted:
        return StatusColors.StatusesPink;
      case SubmissionProfileStatus.DeficienciesFound:
      case SubmissionProfileStatus.Deferred:
        return StatusColors.StatusesDandellion;
      case SubmissionProfileStatus.Approved:
      case SubmissionProfileStatus.ApprovedWithConditions:
        return StatusColors.StatusesGreen;
      case SubmissionProfileStatus.DeemedApproved:
        return StatusColors.StatusesPastelGreen;
      case SubmissionProfileStatus.Withdrawn:
        return StatusColors.StatusesRedTransparent;
      case SubmissionProfileStatus.Rejected:
      case SubmissionProfileStatus.Disapproved:
        return StatusColors.StatusesRed;
    }
  }

  private getColorForIRBSubmissionProfileInvSite(): StatusColors {
    switch (this.statusId) {
      case SubmissionProfileSiteStatus.Pending:
        return StatusColors.StatusesOrange;
      case SubmissionProfileSiteStatus.ApprovalReceived:
        return StatusColors.StatusesGreen;
      case SubmissionProfileSiteStatus.Rejected:
        return StatusColors.StatusesRed;
    }
  }

  private getColorForInvestigativeSite(): StatusColors {
    switch (this.statusId) {
      case InvestigativeSite.Status.Pending:
        return StatusColors.StatusesOrange;
      case InvestigativeSite.Status.Active:
        return StatusColors.StatusesGreen;
      case InvestigativeSite.Status.Enrolling:
        return StatusColors.StatusesBlue;
      case InvestigativeSite.Status.NonParticipating:
        return StatusColors.StatusesDandellion;
      case InvestigativeSite.Status.Rejected:
        return StatusColors.StatusesRed;
      case InvestigativeSite.Status.Maintenance:
        return StatusColors.StatusesPink;
      case InvestigativeSite.Status.Closed:
        return StatusColors.StatusesRed;
      case InvestigativeSite.Status.Selected:
        return StatusColors.StatusesPastelGreen;
      case InvestigativeSite.Status.Terminated:
        return StatusColors.StatusesRedTransparent;
      case InvestigativeSite.Status.Approved:
        return StatusColors.StatusesGreen;
      case InvestigativeSite.Status.Deselected:
        return StatusColors.StatusesGrey;
      case InvestigativeSite.Status.CloseOut:
        return StatusColors.StatusesViolet;
      case InvestigativeSite.Status.OnHold:
        return StatusColors.StatusesRedTransparent;
    }
  }

  private getColorForAmendmentProfile(): StatusColors {
    switch (this.statusId) {
      case AmendmentStatus.Pending:
        return StatusColors.StatusesOrange;
      case AmendmentStatus.Completed:
        return StatusColors.StatusesGreen;
    }
  }

  private getColorForContact(): StatusColors {
    switch (this.statusId) {
      case ContactStatus.Active:
        return StatusColors.StatusesGreen;
      case ContactStatus.Inactive:
        return StatusColors.StatusesOrange;
    }
  }

  private getColorForMissingDocument(): StatusColors {
    return StatusColors.StatusesBlue;
  }

  private getColorForSSUDocument(): StatusColors {
    switch (this.statusId) {
      case SSUDocumentsStatuses.Missing:
        return StatusColors.StatusesBlue;
      case SSUDocumentsStatuses.Pending:
        return StatusColors.StatusesOrange;
      case SSUDocumentsStatuses.Approved:
        return StatusColors.StatusesGreen;
      case SSUDocumentsStatuses.Rejected:
        return StatusColors.StatusesRed;
    }
  }

  // TODO: in work
  private getColorForDocument(): StatusColors {
    switch (this.statusId) {
      case AcknowledgmentDocumentStatus.Pending:
        return StatusColors.StatusesOrange;
      case AcknowledgmentDocumentStatus.InProgress:
        return StatusColors.StatusesDandellion;
      case AcknowledgmentDocumentStatus.Completed:
        return StatusColors.StatusesGreen;
      case AcknowledgmentDocumentStatus.Cancelled:
        return StatusColors.StatusesRed;
      default:
        return StatusColors.StatusesOrange;
    }
  }

  private getColorForStudyProfile(): StatusColors {
    switch (this.statusId) {
      case StudyProfileStatus.Pending:
        return StatusColors.StatusesOrange;
      case StudyProfileStatus.Archived:
        return StatusColors.StatusesGrey;
      case StudyProfileStatus.Canceled:
        return StatusColors.StatusesDandellion;
      case StudyProfileStatus.Closeout:
        return StatusColors.StatusesViolet;
      case StudyProfileStatus.Enrolling:
        return StatusColors.StatusesBlue;
      case StudyProfileStatus.MaintenanceEnrollmentClosed:
        return StatusColors.StatusesRedTransparent;
      case StudyProfileStatus.OnHold:
        return StatusColors.StatusesGrey;
      case StudyProfileStatus.Postponed:
        return StatusColors.StatusesOrange;
      case StudyProfileStatus.ProtocolFinal:
        return StatusColors.StatusesPink;
      case StudyProfileStatus.Randomizing:
        return StatusColors.StatusesViolet;
      case StudyProfileStatus.ScreeningRecruiting:
        return StatusColors.StatusesPink;
      case StudyProfileStatus.StartUp:
        return StatusColors.StatusesBlue;
      default:
        return StatusColors.StatusesPastelGreen;
    }
  }

  private getColorForCollaborationDocumentReviewStatus(): StatusColors {
    switch (this.statusId) {
      case CollaborationDocumentReviewStatus.Pending:
      case CollaborationDocumentReviewStatus.InProgress:
        return StatusColors.StatusesOrange;
      case CollaborationDocumentReviewStatus.Completed:
      case CollaborationDocumentReviewStatus.Closed:
        return StatusColors.StatusesGreen;
    }
  }

  private getColorForCollaborationReviewStatus(): StatusColors {
    switch (this.statusId) {
      case CollaborationReviewStatus.Open:
        return StatusColors.StatusesBlue;
      case CollaborationReviewStatus.InProgress:
        return StatusColors.StatusesOrange;
      case CollaborationReviewStatus.Closed:
        return StatusColors.StatusesGreen;
      case CollaborationReviewStatus.Overdue:
        return StatusColors.StatusesRed;
    }
  }

  private getColorForWorkflowStageStatusColor(): ActionColors {
    switch (this.statusId) {
      case WorkflowActionColor.Green:
        return ActionColors.ActionsGreen;
      case WorkflowActionColor.Orange:
        return ActionColors.ActionsOrange;
      case WorkflowActionColor.Default:
        return ActionColors.ActionsBlue;
      case WorkflowActionColor.Red:
        return ActionColors.ActionsRed;
      default:
        return ActionColors.ActionsOrange;
    }
  }

  private getColorForServerTaskQueueStatus(): StatusColors {
    switch (this.statusId) {
      case ServerTaskQueue.ServerTaskQueueStatus.Pending:
        return StatusColors.StatusesOrange;
      case ServerTaskQueue.ServerTaskQueueStatus.InProgress:
        return StatusColors.StatusesDandellion;
      case ServerTaskQueue.ServerTaskQueueStatus.Completed:
        return StatusColors.StatusesGreen;
      case ServerTaskQueue.ServerTaskQueueStatus.Failed:
        return StatusColors.StatusesRed;
    }
  }
}
